import type { ProgressionStrategy } from './enums.js';
import { effectiveLoadG } from './load.js';
import { workingSets } from './sets.js';
import { roundToIncrement } from './units.js';
import type { LoggedSet, PrescribedSet } from './types.js';

/**
 * Progression.
 *
 * Three properties, all deliberate:
 *
 *  1. **Derived, not stored.** The suggestion is computed from what was
 *     actually performed. A stored "current working weight" drifts out of step
 *     the first time you deload, miss a week, or train the lift outside the
 *     plan — and then confidently tells you the wrong number.
 *  2. **Suggested, never applied.** Nothing here mutates a plan. An app that
 *     quietly raised your working weight after a bad night's sleep would be
 *     worse than no progression at all.
 *  3. **Extensible.** Strategies are entries in a registry, so a new one is an
 *     addition rather than a redesign.
 */

export type SuggestionKind = 'increase' | 'hold' | 'deload' | 'none';

export interface ProgressionSuggestion {
  kind: SuggestionKind;
  /** Load actually used last time, when it could be determined. */
  fromLoadG: number | null;
  /** Suggested load. Equal to `fromLoadG` when holding. */
  toLoadG: number | null;
  /** Applied increment, in grams. Zero unless increasing. */
  incrementG: number;
  /** Rep target to work from next session. */
  targetReps: { min: number | null; max: number | null };
  /** Stable machine-readable code, so the UI can phrase it however it likes. */
  reason:
    | 'no_history'
    | 'no_rule'
    | 'rule_disabled'
    | 'incomplete_sets'
    | 'below_rep_target'
    | 'all_sets_at_rep_max'
    | 'session_completed'
    | 'unknown_load'
    | 'inconsistent_load';
  /** Human-readable explanation, for the "why?" affordance. */
  explanation: string;
}

export interface ProgressionRule {
  strategy: ProgressionStrategy;
  incrementG: number;
  /** Round suggestions to a weight that can actually be loaded on a bar. */
  roundingG: number;
  enabled: boolean;
}

export interface ProgressionInput {
  rule: ProgressionRule;
  /** Per-set prescription for the exercise. */
  prescribed: readonly PrescribedSet[];
  /** Sets performed the last time this exercise was trained. */
  lastPerformance: readonly LoggedSet[];
}

interface StrategyContext {
  rule: ProgressionRule;
  repMin: number | null;
  repMax: number | null;
  prescribedSetCount: number;
  completed: LoggedSet[];
  loads: number[];
  /** Every completed working set shared one load. */
  consistentLoad: number | null;
}

export interface ProgressionStrategyHandler {
  id: ProgressionStrategy;
  name: string;
  evaluate: (context: StrategyContext) => ProgressionSuggestion;
}

function suggestion(
  partial: Pick<ProgressionSuggestion, 'kind' | 'reason' | 'explanation'> &
    Partial<ProgressionSuggestion>,
  context: Pick<StrategyContext, 'repMin' | 'repMax'>,
): ProgressionSuggestion {
  return {
    fromLoadG: null,
    toLoadG: null,
    incrementG: 0,
    targetReps: { min: context.repMin, max: context.repMax },
    ...partial,
  };
}

/**
 * Double progression: work up the rep range at a fixed load, then add weight
 * and drop back to the bottom of the range.
 *
 * This is the strategy behind the approved example — 3 × 8–12, three sets of
 * 12, suggest +2.5 kg.
 */
export const DOUBLE_PROGRESSION: ProgressionStrategyHandler = {
  id: 'double_progression',
  name: 'Double progression',
  evaluate: (context) => {
    const { rule, repMax, prescribedSetCount, completed, consistentLoad } = context;

    if (completed.length < prescribedSetCount) {
      return suggestion(
        {
          kind: 'hold',
          reason: 'incomplete_sets',
          explanation: `Completed ${String(completed.length)} of ${String(prescribedSetCount)} prescribed sets. Repeat the same weight.`,
          fromLoadG: consistentLoad,
          toLoadG: consistentLoad,
        },
        context,
      );
    }

    if (repMax === null) {
      return suggestion(
        {
          kind: 'none',
          reason: 'no_rule',
          explanation: 'Double progression needs an upper rep target.',
        },
        context,
      );
    }

    if (consistentLoad === null) {
      return suggestion(
        {
          kind: 'hold',
          reason: 'inconsistent_load',
          explanation:
            'The working sets used different weights, so there is no single load to progress from.',
        },
        context,
      );
    }

    const allAtTop = completed.every((s) => (s.reps ?? 0) >= repMax);
    if (!allAtTop) {
      const lowest = Math.min(...completed.map((s) => s.reps ?? 0));
      return suggestion(
        {
          kind: 'hold',
          reason: 'below_rep_target',
          explanation: `Lowest set was ${String(lowest)} reps; reach ${String(repMax)} on every set before adding weight.`,
          fromLoadG: consistentLoad,
          toLoadG: consistentLoad,
        },
        context,
      );
    }

    const raw = consistentLoad + rule.incrementG;
    const rounded = roundToIncrement(raw, rule.roundingG);

    return suggestion(
      {
        kind: 'increase',
        reason: 'all_sets_at_rep_max',
        explanation: `Every set reached ${String(repMax)} reps. Add weight and work back up the rep range.`,
        fromLoadG: consistentLoad,
        toLoadG: rounded,
        incrementG: rounded - consistentLoad,
      },
      context,
    );
  },
};

/** Linear: add weight whenever the prescription was completed, whatever the reps. */
export const LINEAR_PROGRESSION: ProgressionStrategyHandler = {
  id: 'linear',
  name: 'Linear',
  evaluate: (context) => {
    const { rule, prescribedSetCount, completed, consistentLoad } = context;

    if (completed.length < prescribedSetCount) {
      return suggestion(
        {
          kind: 'hold',
          reason: 'incomplete_sets',
          explanation: `Completed ${String(completed.length)} of ${String(prescribedSetCount)} prescribed sets. Repeat the same weight.`,
          fromLoadG: consistentLoad,
          toLoadG: consistentLoad,
        },
        context,
      );
    }

    if (consistentLoad === null) {
      return suggestion(
        {
          kind: 'hold',
          reason: 'inconsistent_load',
          explanation: 'The working sets used different weights, so there is no single load to progress from.',
        },
        context,
      );
    }

    const rounded = roundToIncrement(consistentLoad + rule.incrementG, rule.roundingG);
    return suggestion(
      {
        kind: 'increase',
        reason: 'session_completed',
        explanation: 'All prescribed sets completed. Add weight for the next session.',
        fromLoadG: consistentLoad,
        toLoadG: rounded,
        incrementG: rounded - consistentLoad,
      },
      context,
    );
  },
};

export const NO_PROGRESSION: ProgressionStrategyHandler = {
  id: 'none',
  name: 'Manual',
  evaluate: (context) =>
    suggestion(
      {
        kind: 'none',
        reason: 'no_rule',
        explanation: 'Progression is set to manual for this exercise.',
      },
      context,
    ),
};

/**
 * Named `..._HANDLERS` rather than `PROGRESSION_STRATEGIES`, which `enums.ts`
 * already uses for the list of strategy *names*. This is the registry of the
 * implementations behind those names.
 */
export const PROGRESSION_STRATEGY_HANDLERS: Readonly<
  Record<ProgressionStrategy, ProgressionStrategyHandler>
> = Object.freeze({
    none: NO_PROGRESSION,
    linear: LINEAR_PROGRESSION,
    double_progression: DOUBLE_PROGRESSION,
  });

export function getProgressionStrategy(strategy: ProgressionStrategy): ProgressionStrategyHandler {
  return PROGRESSION_STRATEGY_HANDLERS[strategy];
}

/**
 * Compute a suggestion for the next session.
 *
 * **Returns advice. Mutates nothing.** The caller presents it; the user accepts
 * or overrides it.
 */
export function suggestProgression(input: ProgressionInput): ProgressionSuggestion {
  const { rule, prescribed, lastPerformance } = input;

  const primaryPrescribed = prescribed.filter(
    (p) => p.dropIndex === 0 && p.setType !== 'warmup',
  );
  const repMin = primaryPrescribed[0]?.repMin ?? null;
  const repMax = primaryPrescribed[0]?.repMax ?? null;

  const baseContext = {
    rule,
    repMin,
    repMax,
    prescribedSetCount: primaryPrescribed.length,
    completed: [] as LoggedSet[],
    loads: [] as number[],
    consistentLoad: null as number | null,
  };

  if (!rule.enabled) {
    return suggestion(
      {
        kind: 'none',
        reason: 'rule_disabled',
        explanation: 'Progression is switched off for this exercise.',
      },
      baseContext,
    );
  }

  const completed = workingSets(lastPerformance).filter((s) => s.isCompleted);
  if (completed.length === 0) {
    return suggestion(
      {
        kind: 'none',
        reason: 'no_history',
        explanation: 'No previous performance to progress from.',
      },
      baseContext,
    );
  }

  const loads: number[] = [];
  for (const set of completed) {
    const load = effectiveLoadG(set);
    if (!load.known) {
      return suggestion(
        {
          kind: 'hold',
          reason: 'unknown_load',
          explanation:
            'The last session has a set whose load could not be determined, so no suggestion can be made.',
        },
        baseContext,
      );
    }
    loads.push(load.value);
  }

  const first = loads[0] ?? null;
  const consistentLoad = first !== null && loads.every((l) => l === first) ? first : null;

  return getProgressionStrategy(rule.strategy).evaluate({
    ...baseContext,
    completed,
    loads,
    consistentLoad,
  });
}
